import { useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
} from "@tanstack/react-table";
import { ArrowUpDown, Edit, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination as PaginationComponent } from "@/components/common/pagination";
import { FormLeadModal } from "@/components/common/forms";
import { AlertDialogDelete } from "@/components/common/dialogs";
import { Lead } from "@/types";
import { formatPhone } from "@/lib/utils";
import { identityPhone } from "@/lib/identity-validation";

type LeadDataTableProps = {
  data: Lead[];
  paginationData?: any;
  onDelete?: (id: string) => void;
};

export function LeadDataTable({
  data,
  paginationData,
  onDelete
}: LeadDataTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const getStatusBadge = (status?: string) => {
    const statusConfig = {
      new: { label: "Novo", color: "bg-app-info/10 text-app-info" },
      in_progress: { label: "Em Andamento", color: "bg-app-warning/10 text-app-warning" },
      qualified: { label: "Qualificado", color: "bg-app-success/10 text-app-success" },
      converted: { label: "Convertido", color: "bg-brand-soft text-brand-hover" },
    };

    const config = status ? statusConfig[status as keyof typeof statusConfig] : null;
    return config ? (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    ) : (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-app-surface-muted text-app-fg">
        Não definido
      </span>
    );
  };

  const getSourceBadge = (source?: string) => {
    const sourceConfig = {
      site: { label: "Site", color: "bg-app-info/10 text-app-info" },
      indicacao: { label: "Indicação", color: "bg-app-success/10 text-app-success" },
      rede_social: { label: "Rede Social", color: "bg-brand-soft text-brand-hover" },
      email: { label: "E-mail", color: "bg-app-surface-muted text-app-fg" },
      telefone: { label: "Telefone", color: "bg-app-warning/10 text-app-warning" },
      evento: { label: "Evento", color: "bg-brand-soft text-brand-hover" },
      whatsapp: { label: "WhatsApp", color: "bg-app-success/10 text-app-success" },
      outro: { label: "Outro", color: "bg-app-surface-muted text-app-fg" },
    };

    const config = source ? sourceConfig[source as keyof typeof sourceConfig] : null;
    return config ? (
      <span className={`px-2 py-1 rounded text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    ) : (
      <span className="text-app-fg-muted text-xs">-</span>
    );
  };

  const formatCurrency = (value?: number) => {
    if (!value) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const columns: ColumnDef<Lead>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 font-semibold"
        >
          Nome
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="font-medium">
          {row.getValue("name")}
          {row.original.company && (
            <div className="text-sm text-app-fg-muted">{row.original.company}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: "email",
      header: "E-mail",
      cell: ({ row }) => (
        <div className="text-sm">
          {row.getValue("email") || <span className="text-app-fg-muted">-</span>}
        </div>
      ),
    },
    {
      accessorKey: "phone",
      header: "Telefone",
      cell: ({ row }) => (
        <div className="text-sm">
          {formatPhone(identityPhone(row.getValue("phone")))}
        </div>
      ),
    },
    {
      accessorKey: "source",
      header: "Origem",
      cell: ({ row }) => getSourceBadge(String(row.getValue("source")).toLowerCase()),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => getStatusBadge(String(row.getValue("status"))),
    },
    {
      accessorKey: "estimated_value",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 font-semibold"
        >
          Valor Estimado
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="text-sm font-medium">
          {formatCurrency(row.getValue("estimated_value"))}
        </div>
      ),
    },
    {
      accessorKey: "converted_to_patient",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 font-semibold"
        >
          Convertido para Paciente
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="text-sm font-medium">
          {row.getValue("converted_to_patient") ? "Sim" : "Não"}
        </div>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Data Cadastro",
      cell: ({ row }) => {
        const date = new Date(row.getValue("created_at"));
        return (
          <div className="text-sm">
            {format(date, "dd/MM/yyyy", { locale: ptBR })}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Ações",
      enableHiding: false,
      cell: ({ row }) => {
        const lead = row.original;

        return (
          <div className="flex items-center gap-2">
            {/* C-19: the button opens the lead form, which saves through
                `PUT /clinic/crm/leads/{id}`. It used to also call an `onEdit`
                callback that only wrote to the console. */}
            <FormLeadModal lead={lead}>
              <Button variant="ghost" size="sm" aria-label="Editar lead">
                <Edit className="h-4 w-4" />
              </Button>
            </FormLeadModal>

            <AlertDialogDelete
              endpoint={`/clinic/crm/leads/${lead.id}`}
              queryKey="leads"
              callBack={() => {
                onDelete?.(lead.id);
              }}
            >
              <Button
                variant="ghost"
                size="sm"
                className="text-app-danger hover:text-app-danger hover:bg-app-danger/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogDelete>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Nenhum lead encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {paginationData && (
        <PaginationComponent
          data={paginationData}
          className="mt-4"
        />
      )}
    </div>
  );
}